import React, { useState } from 'react';
import { Search, ShieldCheck, ShieldOff, UserCircle, Clock, Loader2, X, ChevronDown, BookOpen, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITLEMENT_TYPES = [
  { value: 'course_access', label: 'Full Course Access' },
  { value: 'module_access', label: 'Module Access' },
  { value: 'ebook_access', label: 'eBook Access' },
];

const StatusPill = ({ status }) => {
  const map = {
    ACTIVE:               'bg-teal-500/10 text-teal-400 border-teal-500/20',
    active:               'bg-teal-500/10 text-teal-400 border-teal-500/20',
    revoked:              'bg-red-500/10 text-red-400 border-red-500/20',
    pending_registration: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  };
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${map[status] ?? 'bg-cream/[0.05] text-cream/30 border-cream/[0.06]'}`}>
      {status}
    </span>
  );
};

/* ── Grant Access Modal ── */
const GrantModal = ({ email, onClose, onSuccess }) => {
  const [type, setType] = useState('course_access');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const handleGrant = async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/cms/payments/reader-access/${encodeURIComponent(email)}/grant?entitlement_type=${type}`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) { onSuccess(); onClose(); }
      else { const d = await r.json(); setErr(d.detail || 'Failed'); }
    } catch (e) { setErr(String(e)); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-navy border border-gold/[0.08] rounded-xl max-w-sm w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-cream/70 text-sm font-medium">Grant Access</p>
          <button onClick={onClose} className="text-cream/30 hover:text-cream/60"><X size={15} /></button>
        </div>
        <p className="text-cream/30 text-xs mb-3">Granting access to <strong className="text-cream/50">{email}</strong></p>
        <label className="text-cream/30 text-[10px] uppercase tracking-wide block mb-1">Access Type</label>
        <select value={type} onChange={e => setType(e.target.value)} data-testid="grant-type-select"
          className="w-full bg-navy-deep border border-cream/[0.08] rounded-lg px-3 py-2 text-cream/60 text-xs mb-4 focus:outline-none focus:border-gold/30">
          {ENTITLEMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {err && <p className="text-red-400 text-xs mb-3">{err}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 text-xs text-cream/30 border border-cream/[0.06] rounded-lg hover:text-cream/60">Cancel</button>
          <button onClick={handleGrant} disabled={loading} data-testid="grant-confirm-btn"
            className="flex-1 py-2 text-xs bg-teal-500/10 border border-teal-500/20 text-teal-300 hover:bg-teal-500/15 rounded-lg disabled:opacity-50">
            {loading ? <Loader2 size={12} className="animate-spin mx-auto" /> : 'Grant Access'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Reader Profile Card ── */
const ReaderProfile = ({ profile, onGrant, onRevoke, onRefresh, actionLoading, toast }) => {
  const { user, entitlements, pending_entitlements } = profile;
  const hasAny = (entitlements?.length > 0) || (pending_entitlements?.length > 0);
  const activeEnts = entitlements?.filter(e => e.status === 'ACTIVE' || e.status === 'active') || [];

  return (
    <div className="mt-5 bg-navy border border-gold/[0.06] rounded-xl overflow-hidden" data-testid="reader-profile">
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b border-gold/[0.04]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-gold/[0.1] border border-gold/[0.08] flex items-center justify-center flex-shrink-0">
            <span className="text-gold/60 text-sm font-medium">
              {user ? (user.first_name?.[0] || user.email[0]).toUpperCase() : '?'}
            </span>
          </div>
          <div>
            {user ? (
              <>
                <p className="text-cream/70 text-sm font-medium">{user.first_name} {user.last_name}</p>
                <p className="text-cream/30 text-xs">{user.email}</p>
                <p className="text-cream/15 text-[10px] mt-0.5">Joined {new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </>
            ) : (
              <>
                <p className="text-cream/40 text-sm">No account registered</p>
                <p className="text-cream/20 text-xs">Pending entitlements will activate on sign-up</p>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRefresh} className="w-7 h-7 flex items-center justify-center rounded-lg text-cream/20 hover:text-cream/50 hover:bg-cream/[0.05] transition-colors">
            <RefreshCw size={12} />
          </button>
          <button onClick={onGrant} data-testid="grant-access-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/[0.08] border border-teal-500/20 text-teal-300 hover:bg-teal-500/[0.12] text-xs transition-all">
            <ShieldCheck size={11} /> Grant Access
          </button>
          {activeEnts.length > 0 && (
            <button onClick={onRevoke} disabled={actionLoading === 'revoke'} data-testid="revoke-all-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/[0.06] border border-red-500/15 text-red-400/70 hover:bg-red-500/[0.1] hover:text-red-400 text-xs transition-all disabled:opacity-50">
              {actionLoading === 'revoke' ? <Loader2 size={11} className="animate-spin" /> : <ShieldOff size={11} />}
              Revoke All
            </button>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mx-5 mt-3 px-3 py-2 rounded-lg text-xs ${toast.type === 'ok' ? 'bg-teal-500/10 border border-teal-500/20 text-teal-300' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          {toast.msg}
        </div>
      )}

      {/* Entitlements */}
      <div className="px-5 py-4">
        {!hasAny ? (
          <div className="flex items-center gap-2 text-cream/20 py-4 justify-center">
            <BookOpen size={16} />
            <p className="text-xs">No access entitlements found for this reader</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entitlements?.length > 0 && (
              <div>
                <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-2">Entitlements</p>
                <div className="space-y-2">
                  {entitlements.map(e => (
                    <div key={e.id} className="flex items-center justify-between p-3 bg-navy-deep rounded-xl border border-gold/[0.03]" data-testid={`entitlement-row-${e.id}`}>
                      <div>
                        <p className="text-cream/60 text-xs font-medium">{e.entitlement_type}</p>
                        {e.created_at && <p className="text-cream/20 text-[10px] mt-0.5">Since {new Date(e.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
                        {e.revoked_at && <p className="text-cream/15 text-[10px]">Revoked {new Date(e.revoked_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
                      </div>
                      <StatusPill status={e.status} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {user?.purchased_modules?.length > 0 && (
              <div>
                <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-2">Purchased Modules</p>
                <div className="flex flex-wrap gap-2">
                  {user.purchased_modules.map(m => (
                    <span key={m} className="text-[10px] px-2 py-1 rounded-lg bg-gold/[0.07] border border-gold/[0.1] text-gold/60">{m}</span>
                  ))}
                </div>
              </div>
            )}

            {pending_entitlements?.length > 0 && (
              <div>
                <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-2">Pending (no account yet)</p>
                <div className="space-y-1.5">
                  {pending_entitlements.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-2.5 bg-amber-500/[0.03] rounded-xl border border-amber-500/[0.08]">
                      <p className="text-cream/40 text-xs">{p.entitlement_type}</p>
                      <StatusPill status={p.status} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Main Component ── */
const AdminReaders = () => {
  const [query, setQuery] = useState('');
  const [profile, setProfile] = useState(null);
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'ok') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const fetchProfile = async (email) => {
    setSearching(true); setNotFound(false); setProfile(null);
    try {
      const r = await fetch(`${API_URL}/api/cms/payments/reader-access/${encodeURIComponent(email.trim())}`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        if (!d.user && !d.entitlements?.length && !d.pending_entitlements?.length) { setNotFound(true); }
        else { setProfile(d); }
      } else if (r.status === 404) { setNotFound(true); }
    } catch {}
    setSearching(false);
  };

  const handleSearch = (e) => { e.preventDefault(); if (query.trim()) fetchProfile(query); };

  const handleRevoke = async () => {
    if (!profile || !window.confirm(`Revoke ALL access for ${query.trim()}?`)) return;
    setActionLoading('revoke');
    try {
      const r = await fetch(`${API_URL}/api/cms/payments/reader-access/${encodeURIComponent(query.trim())}/revoke`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) { showToast('Access revoked'); fetchProfile(query.trim()); }
      else showToast('Failed to revoke', 'err');
    } finally { setActionLoading(null); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-readers-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-cream text-xl font-serif font-light">Reader Access</h1>
        <p className="text-cream/25 text-xs mt-0.5">Look up any reader by email to view, grant, or revoke course access</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="flex-1 relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/20" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Enter reader email address…"
            type="email"
            className="w-full bg-navy border border-cream/[0.07] rounded-xl pl-9 pr-4 py-2.5 text-cream/70 text-sm placeholder-cream/15 focus:outline-none focus:border-gold/30 transition-colors"
            data-testid="reader-search-input"
          />
        </div>
        <button type="submit" disabled={searching || !query.trim()} data-testid="reader-search-btn"
          className="px-4 py-2.5 bg-gold/[0.1] border border-gold/[0.15] text-gold/70 hover:text-gold hover:bg-gold/[0.15] rounded-xl text-sm transition-all disabled:opacity-40">
          {searching ? <Loader2 size={14} className="animate-spin" /> : 'Look up'}
        </button>
      </form>

      {/* Not found */}
      {notFound && (
        <div className="mt-5 flex items-center gap-3 p-4 bg-navy border border-cream/[0.05] rounded-xl" data-testid="reader-not-found">
          <UserCircle size={18} className="text-cream/15 flex-shrink-0" />
          <div>
            <p className="text-cream/40 text-sm">No record found for <strong className="text-cream/50">{query}</strong></p>
            <p className="text-cream/20 text-xs mt-0.5">They may not have registered yet. You can still pre-grant access below — it will activate when they sign up.</p>
          </div>
          <button onClick={() => setShowGrant(true)} data-testid="grant-access-noaccount-btn"
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/[0.08] border border-teal-500/20 text-teal-300 hover:bg-teal-500/[0.12] text-xs transition-all ml-auto">
            <ShieldCheck size={11} /> Pre-grant
          </button>
        </div>
      )}

      {/* Profile */}
      {profile && (
        <ReaderProfile
          profile={profile}
          onGrant={() => setShowGrant(true)}
          onRevoke={handleRevoke}
          onRefresh={() => fetchProfile(query)}
          actionLoading={actionLoading}
          toast={toast}
        />
      )}

      {showGrant && (
        <GrantModal
          email={query.trim()}
          onClose={() => setShowGrant(false)}
          onSuccess={() => { showToast('Access granted'); fetchProfile(query.trim()); }}
        />
      )}
    </div>
  );
};

export default AdminReaders;
